import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, MapPin, Clock, BookOpen, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import CookieBanner from "@/components/CookieBanner";

const WHATSAPP_URL = "https://wa.me/34672972514?text=Hola%2C%20he%20leido%20tu%20articulo%20sobre%20KPIs%20y%20quiero%20revisar%20los%20de%20mi%20empresa";
const LOGO_URL = "/datyon-logo.png";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } }
};

const FAQS = [
  {
    q: "¿Cuántos KPIs debe medir una empresa pequeña?",
    a: "Lo importante no es la cantidad sino el foco. Una PYME suele necesitar entre 6 y 10 KPIs bien elegidos: margen, CAC, ticket medio, rentabilidad por producto, costes operativos y flujo de caja. Con eso cubres la mayoría de decisiones que mueven dinero."
  },
  {
    q: "¿Cuál es el KPI más importante para saber si una empresa es rentable?",
    a: "El margen de beneficio neto. Es el que te dice cuánto ganas realmente después de cubrir todos los costes. Una empresa puede facturar mucho y tener un margen muy bajo: el problema casi nunca es vender más, sino vender mejor."
  },
  {
    q: "¿Necesito un software caro para medir los KPIs de mi empresa?",
    a: "No. Empieza con lo que ya tienes: ERP, facturación, CRM y hojas de cálculo. Cuando los datos crecen, herramientas como Power BI permiten centralizar todo en un dashboard sin grandes inversiones. La clave es el análisis, no la herramienta."
  },
  {
    q: "¿Con qué frecuencia debería revisar los KPIs de mi empresa?",
    a: "Los operativos (ventas, caja, costes) idealmente semanales; los estratégicos (rentabilidad por producto, CAC) mensuales. Si esperas al cierre del año para mirarlos, los problemas ya llevan meses costándote dinero."
  },
  {
    q: "¿Qué hago si los KPIs me dicen que mi empresa no es rentable?",
    a: "Localizar la fuga: producto, cliente o proceso. Una vez identificado el origen, el plan es siempre el mismo orden: cortar lo que no funciona, optimizar lo que sí, y reinvertir el ahorro en lo más rentable. Aquí es donde el análisis de datos marca la diferencia."
  }
];

export default function BlogArticle3() {
  useEffect(() => {
    document.title = "Qué KPIs debe medir una empresa para ser rentable | Datyon";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Los KPIs que toda empresa debe medir para saber si es rentable: margen, CAC, ticket medio, rentabilidad por producto, costes y flujo de caja. Guía práctica orientada a negocio."
      );
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", "https://datyon.com/blog/que-kpis-medir-empresa-rentable");
    }

    // FAQPage JSON-LD
    const ldScript = document.createElement("script");
    ldScript.type = "application/ld+json";
    ldScript.id = "kpis-faq-jsonld";
    ldScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a }
      }))
    });
    document.head.appendChild(ldScript);

    window.scrollTo(0, 0);
    return () => {
      document.title = "Análisis de Datos en Murcia para Empresas | Datyon";
      if (metaDesc) metaDesc.setAttribute("content", "Transforma los datos de tu empresa en decisiones rentables.");
      if (canonical) canonical.setAttribute("href", "https://datyon.com/");
      const existing = document.getElementById("kpis-faq-jsonld");
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#020617] relative overflow-x-hidden" data-testid="blog-article3-page">
      <div className="noise-overlay" />

      <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="article3-navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center pl-4">
              <img src={LOGO_URL} alt="DATYON" className="h-20 w-auto" style={{ maxHeight: '80px' }} />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Inicio</Link>
              <Link to="/servicios" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Servicios</Link>
              <Link to="/sobre-mi" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Sobre mí</Link>
              <Link to="/blog" className="nav-link text-indigo-400 hover:text-white text-sm font-medium transition-colors">Blog</Link>
              <Link to="/contacto" className="nav-link text-slate-300 hover:text-white text-sm font-medium transition-colors">Contacto</Link>
            </div>
          </div>
        </div>
      </nav>

      <main>
        <article className="relative section-padding pt-32 overflow-hidden" data-testid="article3-content">
          <div className="spotlight-1 -top-40 -left-40" />
          <div className="max-w-3xl mx-auto relative z-10">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
              <motion.div variants={fadeInUp} className="mb-8">
                <Link to="/blog" className="inline-flex items-center text-slate-400 hover:text-indigo-400 text-sm transition-colors mb-6">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver al blog
                </Link>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-xs font-medium text-indigo-400 bg-indigo-400/10 px-3 py-1 rounded-full">KPIs y rentabilidad</span>
                  <span className="text-xs text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" /> 6 min de lectura</span>
                </div>
              </motion.div>

              <motion.h1 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-8">
                Qué KPIs debe medir una empresa para ser rentable
              </motion.h1>

              <motion.div variants={fadeInUp} className="prose-custom text-slate-400 text-base leading-relaxed space-y-5">
                <p className="text-lg text-slate-300">
                  Muchas empresas creen que su negocio funciona bien porque facturan.
                </p>

                <p>Pero facturar no es lo mismo que ser rentable.</p>

                <p>
                  Puedes tener ventas, clientes y movimiento… y aun así estar perdiendo dinero sin darte cuenta.
                </p>

                <p>
                  El problema es que muchas decisiones se toman sin tener claro qué está pasando realmente en el negocio.
                </p>

                <p>Y ahí es donde entran los KPIs.</p>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">Qué es un KPI (explicado fácil)</h2>

                <p>
                  Un KPI (Key Performance Indicator) es simplemente una métrica que te dice si tu empresa va bien o no.
                </p>

                <p>No necesitas medirlo todo.</p>

                <p>Necesitas medir lo que realmente impacta en tu dinero.</p>

                <p>
                  Porque si no sabes qué números importan, estás tomando decisiones a ciegas.
                </p>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">KPIs clave que toda empresa debería medir</h2>

                <p>
                  Estos son los seis KPIs que más impacto tienen en la rentabilidad de una empresa, sea del tamaño que sea.
                </p>

                <h3 className="text-xl font-bold text-white mt-8 mb-3">1. Margen de beneficio</h3>

                <p>Es el dinero que realmente ganas después de cubrir costes.</p>

                <p>No importa cuánto factures.</p>

                <p>Si el margen es bajo, el negocio no está optimizado.</p>

                <p>Muchas empresas venden mucho… pero ganan poco.</p>

                <h3 className="text-xl font-bold text-white mt-8 mb-3">2. Coste de adquisición de cliente (CAC)</h3>

                <p>¿Cuánto te cuesta conseguir un cliente?</p>

                <p>
                  Si no lo sabes, puedes estar gastando más en captar clientes de lo que luego ganas con ellos.
                </p>

                <p>
                  Y eso es una de las formas más comunes de perder dinero sin darte cuenta.
                </p>

                <h3 className="text-xl font-bold text-white mt-8 mb-3">3. Ticket medio</h3>

                <p>Es cuánto gasta cada cliente en promedio.</p>

                <p>
                  Pequeños cambios aquí pueden tener un impacto enorme en la rentabilidad.
                </p>

                <p>
                  Subir el ticket medio suele ser más fácil que conseguir más clientes.
                </p>

                <h3 className="text-xl font-bold text-white mt-8 mb-3">4. Rentabilidad por producto o servicio</h3>

                <p>No todo lo que vendes es igual de rentable.</p>

                <p>
                  Algunas líneas de negocio pueden estar funcionando peor de lo que parece.
                </p>

                <p>
                  Si no lo analizas, puedes estar dedicando tiempo y recursos a lo que menos te aporta.
                </p>

                <h3 className="text-xl font-bold text-white mt-8 mb-3">5. Costes operativos</h3>

                <p>Aquí es donde se esconden muchas pérdidas.</p>

                <p>Procesos manuales, tareas repetitivas, errores…</p>

                <p>Todo eso suma.</p>

                <p>Y muchas veces no se mide.</p>

                <h3 className="text-xl font-bold text-white mt-8 mb-3">6. Flujo de caja</h3>

                <p>
                  Puedes ser rentable sobre el papel… y tener problemas de liquidez.
                </p>

                <p>
                  Controlar el flujo de caja es clave para la estabilidad del negocio.
                </p>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">El problema no es medir… es entender</h2>

                <p>Muchas empresas tienen datos.</p>

                <p>Incluso tienen dashboards.</p>

                <p>Pero no saben responder preguntas básicas:</p>

                <ul className="list-disc list-inside space-y-2 pl-2">
                  <li>¿Dónde gano dinero?</li>
                  <li>¿Dónde lo pierdo?</li>
                  <li>¿Qué debería cambiar?</li>
                </ul>

                <p>El problema no es la herramienta.</p>

                <p>Es el análisis.</p>

                <p>Un dashboard sin interpretación es solo un gráfico bonito.</p>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">Errores comunes al trabajar con KPIs</h2>

                <ul className="list-disc list-inside space-y-2 pl-2">
                  <li>Medir demasiadas cosas sin foco</li>
                  <li>No conectar los datos con decisiones</li>
                  <li>Usar dashboards sin entenderlos</li>
                  <li>Basarse en intuición en lugar de datos</li>
                </ul>

                <p>Y el más importante:</p>

                <p>
                  Tener información… pero no usarla para mejorar el negocio.
                </p>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">Cómo usar los KPIs para mejorar tu empresa</h2>

                <p>Los KPIs no sirven para "mirar datos".</p>

                <p>Sirven para tomar decisiones.</p>

                <p>Por ejemplo:</p>

                <ul className="list-disc list-inside space-y-2 pl-2">
                  <li>Detectar dónde estás perdiendo dinero</li>
                  <li>Identificar qué productos son realmente rentables</li>
                  <li>Optimizar costes</li>
                  <li>Mejorar la eficiencia</li>
                </ul>

                <p>
                  Aquí es donde el{" "}
                  <Link to="/analisis-datos-empresas" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                    análisis de datos para empresas
                  </Link>{" "}
                  y el{" "}
                  <Link to="/business-intelligence-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                    business intelligence en Murcia
                  </Link>{" "}
                  marcan la diferencia.
                </p>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">Cómo te ayuda el análisis de datos</h2>

                <p>No se trata de tener más herramientas.</p>

                <p>Se trata de entender mejor tu negocio.</p>

                <p>El análisis de datos te permite:</p>

                <ul className="list-disc list-inside space-y-2 pl-2">
                  <li>Ver lo que ahora no estás viendo</li>
                  <li>Tomar decisiones con criterio</li>
                  <li>Detectar problemas antes de que crezcan</li>
                </ul>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">Calcula cuánto dinero podrías estar perdiendo</h2>

                <p>
                  Si no estás midiendo bien estos KPIs, es muy probable que haya ineficiencias en tu empresa.
                </p>

                <p>
                  Puedes calcularlo en menos de un minuto con la{" "}
                  <Link to="/calculadora-roi-power-bi-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                    calculadora ROI Power BI Murcia
                  </Link>
                  : te dice una estimación honesta de cuánto dinero podría estar perdiendo tu empresa por falta de visibilidad de datos.
                </p>

                <h2 className="text-2xl font-bold text-white mt-12 mb-4">Conclusión</h2>

                <p>No necesitas más datos.</p>

                <p>Necesitas entender mejor los que ya tienes.</p>

                <p>
                  Si no sabes qué medir ni cómo interpretar esos números, estás tomando decisiones a ciegas.
                </p>
              </motion.div>

              {/* FAQ */}
              <motion.div variants={fadeInUp} className="mt-16">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6" data-testid="article3-faq-h2">
                  Preguntas frecuentes sobre <span className="text-gradient">KPIs y rentabilidad</span>
                </h2>
                <div className="space-y-4">
                  {FAQS.map((faq, i) => (
                    <Card key={i} className="bg-slate-900 border-slate-800" data-testid={`article3-faq-${i}`}>
                      <CardContent className="p-6">
                        <h3 className="font-semibold text-white mb-3 flex items-start gap-3">
                          <HelpCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                          {faq.q}
                        </h3>
                        <p className="text-slate-400 text-sm leading-relaxed pl-8">{faq.a}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </motion.div>

              {/* CTA */}
              <motion.div variants={fadeInUp} className="mt-16 glass-card rounded-2xl p-8 md:p-10 text-center" data-testid="article3-cta">
                <h2 className="text-2xl font-bold text-white mb-3">
                  En 30 minutos te digo dónde está perdiendo dinero <span className="text-gradient">tu empresa</span>
                </h2>
                <p className="text-slate-400 mb-6 max-w-xl mx-auto">
                  Si quieres saber si los KPIs de tu empresa son los correctos y cómo interpretarlos para mejorar la rentabilidad, hablemos. Sin compromiso, sin coste.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild className="btn-glow bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 rounded-full" data-testid="article3-cta-contact">
                    <Link to="/contacto">
                      Solicita tu diagnóstico gratuito
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                  <Button asChild className="bg-slate-800 hover:bg-slate-700 text-white px-8 py-6 rounded-full border border-slate-700" data-testid="article3-cta-roi">
                    <Link to="/calculadora-roi-power-bi-murcia">
                      Calcular mi ROI primero
                    </Link>
                  </Button>
                </div>
              </motion.div>

              {/* Related links */}
              <motion.div variants={fadeInUp} className="mt-12 text-sm text-slate-500">
                <p>
                  Artículos relacionados:{" "}
                  <Link to="/blog/como-saber-si-tu-empresa-pierde-dinero" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                    Cómo saber si tu empresa está perdiendo dinero
                  </Link>{" · "}
                  <Link to="/blog/analisis-datos-empresas-decisiones" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                    Cómo mejorar decisiones empresariales con datos
                  </Link>
                </p>
                <p className="mt-2">
                  Servicios:{" "}
                  <Link to="/analisis-datos-empresas" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                    Análisis de datos para empresas
                  </Link>{" · "}
                  <Link to="/business-intelligence-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                    Business Intelligence en Murcia
                  </Link>{" · "}
                  <Link to="/calculadora-roi-power-bi-murcia" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2">
                    Calculadora ROI Power BI Murcia
                  </Link>
                </p>
              </motion.div>
            </motion.div>
          </div>
        </article>
      </main>

      <footer className="py-8 px-4 border-t border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="DATYON" className="h-8 w-auto" />
              <div className="flex items-center gap-2 text-slate-400">
                <MapPin className="w-4 h-4 text-indigo-400" />
                <span className="text-sm">Murcia, España</span>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-slate-500 hover:text-white text-sm transition-colors">Inicio</Link>
              <Link to="/servicios" className="text-slate-500 hover:text-white text-sm transition-colors">Servicios</Link>
              <Link to="/blog" className="text-slate-500 hover:text-white text-sm transition-colors">Blog</Link>
              <Link to="/contacto" className="text-slate-500 hover:text-white text-sm transition-colors">Contacto</Link>
            </div>
          </div>
          <div className="flex flex-wrap justify-center md:justify-start gap-4 mb-6 pt-4 border-t border-slate-800">
            <Link to="/aviso-legal" className="text-slate-500 hover:text-white text-sm transition-colors">Aviso Legal</Link>
            <span className="text-slate-700">|</span>
            <Link to="/politica-privacidad" className="text-slate-500 hover:text-white text-sm transition-colors">Política de Privacidad</Link>
            <span className="text-slate-700">|</span>
            <Link to="/politica-cookies" className="text-slate-500 hover:text-white text-sm transition-colors">Política de Cookies</Link>
          </div>
          <p className="text-slate-500 text-sm text-center md:text-left">
            &copy; {new Date().getFullYear()} DATYON - Antonio Oliver. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg shadow-green-500/30 transition-all duration-300 hover:scale-110 group"
        aria-label="Contactar por WhatsApp">
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
      </a>

      <CookieBanner />
    </div>
  );
}
